import { useCallback, useEffect, useRef, useState } from 'react';
import AuthContext from './AuthContext';
import User from '../interfaces/User';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from '../config/msalConfig';
import { createUserIfNotExisting, getUser } from '../services/userService';
import {
  clearPartnerSession,
  getPartnerSession,
} from '../services/partnerSession';
import { useAnalytics } from '../hooks/useAnalytics';
import { useTranslation } from '../hooks/useTranslation';
import { AnalyticsEvents } from '../enums/AnalyticsEvents';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import Loading from '../components/shared/Loading/Loading';
import { detectEmbedMode, getEmbedHomePath } from '../helpers/embedMode';
import { isLocalHost } from '../helpers/environmentHelper';

// simple sleep helper
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

interface AuthProviderProps {
  children: React.ReactNode;
}

const tenantName = isLocalHost()
  ? process.env.REACT_APP_SBX_AD_B2C_TENANT_NAME
  : process.env.REACT_APP_PRD_AD_B2C_TENANT_NAME;

const AuthProvider = ({ children }: AuthProviderProps) => {
  const { instance, inProgress } = useMsal();
  const { t } = useTranslation();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const { track, identify, alias } = useAnalytics();
  const [isLoading, setIsLoading] = useState(() => !detectEmbedMode());
  const [showRetryModal, setShowRetryModal] = useState(false);

  const isNew = useRef<boolean>(false);
  const userInitialized = useRef<boolean>(false);
  const signUpStarted = useRef(false);
  const signInStarted = useRef(false);

  // A partner-iframe session (MixVoIP) has no MSAL account: the redeemed
  // token is an Elora session JWT, not a B2C token. This fetches the real
  // user (and their permissions) using that token, which apiService already
  // prefers over MSAL. Covers both the initial redeem and a page reload
  // while a valid partner session is still in sessionStorage.
  const loadPartnerSessionUser = useCallback(async (): Promise<boolean> => {
    if (currentUser) return true;

    const session = getPartnerSession();
    if (!session?.userId) return false;

    try {
      const result = await getUser(session.userId);
      const user = (result?.message ?? null) as User | null;
      if (!user) {
        clearPartnerSession();
        return false;
      }

      userInitialized.current = true;
      setCurrentUser(user);
      return true;
    } catch (err) {
      console.error('Failed to load partner session user:', err);
      clearPartnerSession();
      return false;
    }
  }, [currentUser]);

  const initializeUser = useCallback(async () => {
    // avoid re-running if user already set or no instance
    if (currentUser || userInitialized.current || !instance) {
      setIsLoading(false);
      return;
    }

    const account = instance.getActiveAccount();
    if (!account) {
      await loadPartnerSessionUser();
      setIsLoading(false);
      return;
    }

    const inviteToken =
      sessionStorage.getItem('elora_invite_token') ?? undefined;

    let user: User | null = null;
    let isNewUser = false;

    // retry up to 5 times
    for (let attempt = 1; attempt <= 5; attempt++) {
      try {
        const [fetchedUser, fresh] = await createUserIfNotExisting(
          account,
          inviteToken
        );
        if (fetchedUser) {
          user = fetchedUser;
          isNewUser = fresh;
          if (fresh && inviteToken) {
            sessionStorage.removeItem('elora_invite_token');
          }
          break;
        }
      } catch (err) {
        console.error(`createUserIfNotExisting attempt ${attempt} failed`, err);
        if (attempt < 5) {
          // back off before retrying
          await sleep(1000 * attempt);
          continue;
        } else {
          // after 5 failures, show modal
          setShowRetryModal(true);
          setIsLoading(false);
          return;
        }
      }
    }

    if (!user) {
      setIsLoading(false);
      return;
    }

    // mark user as initialized
    isNew.current = isNewUser;
    userInitialized.current = true;

    // analytics
    if (isNewUser) {
      track(AnalyticsEvents.SIGN_UP);
      alias(user.id);
    } else {
      track(AnalyticsEvents.SIGN_IN);
      identify(user.id, {
        $name: user.name,
        $role: user.role,
        $company_id: user.company_id,
      });
    }

    setCurrentUser(user);
    setIsLoading(false);
  }, [instance, currentUser, track, alias, identify, loadPartnerSessionUser]);

  const login = useCallback(async () => {
    if (instance.getActiveAccount() || inProgress !== InteractionStatus.None) {
      return;
    }

    if (!currentUser) {
      signUpStarted.current = true;
    } else {
      signInStarted.current = true;
    }

    const isEmbed = detectEmbedMode();

    try {
      if (isEmbed) {
        const response = await instance.loginPopup(loginRequest);
        if (response?.account) {
          instance.setActiveAccount(response.account);
        }
        signUpStarted.current = false;
        signInStarted.current = false;
        userInitialized.current = false;
        setIsLoading(true);
        await initializeUser();
        return;
      }

      instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login failed:', error);
      signUpStarted.current = false;
      signInStarted.current = false;
    }
  }, [instance, inProgress, currentUser, initializeUser]);

  const logout = async () => {
    setCurrentUser(null);
    userInitialized.current = false;

    const isEmbed = detectEmbedMode();
    const defaultLogoutUri = isLocalHost()
      ? 'http://localhost:3000'
      : 'https://app.elora.lu';
    const authority = `https://${tenantName}.b2clogin.com/${tenantName}.onmicrosoft.com/B2C_1_signupsignin`;

    if (isEmbed) {
      const account = instance.getActiveAccount();

      try {
        await instance.logoutPopup({
          account: account ?? undefined,
          postLogoutRedirectUri: `${defaultLogoutUri}/dashboard`,
          authority,
        });
      } catch (error) {
        console.error('Embed logout failed:', error);
        try {
          await instance.logoutRedirect({
            account: account ?? undefined,
            postLogoutRedirectUri: `${defaultLogoutUri}/dashboard`,
            authority,
            onRedirectNavigate: () => false,
          });
        } catch (clearError) {
          console.error('Embed local logout failed:', clearError);
        }
      }

      window.location.assign(getEmbedHomePath());
      return;
    }

    instance
      .logoutRedirect({
        postLogoutRedirectUri: defaultLogoutUri,
        authority,
      })
      .catch(console.error);
  };

  const trackAbandonedEvents = useCallback(() => {
    // On unmount, if sign up or sign in was started but not completed, fire abandoned events
    if (signUpStarted.current) {
      track(AnalyticsEvents.SIGN_UP_ABANDONED);
      signUpStarted.current = false;
    }
    if (signInStarted.current) {
      track(AnalyticsEvents.SIGN_IN_ABANDONED);
      signInStarted.current = false;
    }
  }, [track]);

  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 3;
    const retryDelay = 500; // ms

    const tryHandleRedirect = async () => {
      attempts++;
      try {
        const response = await instance.handleRedirectPromise();
        if (response?.account) {
          instance.setActiveAccount(response.account);
        } else {
          const accounts = instance.getAllAccounts();
          if (accounts.length === 1) {
            instance.setActiveAccount(accounts[0]);
          }
        }

        const hasSession = Boolean(instance.getActiveAccount());
        if (hasSession) {
          setIsLoading(true);
        }

        signUpStarted.current = false;
        signInStarted.current = false;
        initializeUser();
        setIsLoading(false);
      } catch (error) {
        console.error(
          `handleRedirectPromise attempt ${attempts} error:`,
          error
        );
        if (attempts < maxAttempts) {
          await sleep(retryDelay);
          tryHandleRedirect();
        } else {
          setIsLoading(false);
        }
      }
    };

    tryHandleRedirect();

    return () => {
      trackAbandonedEvents();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = () => {
    window.location.reload();
  };

  // show a simple loader while initializing
  if (isLoading && !showRetryModal) {
    return <Loading />;
  }

  return (
    <>
      <AuthContext.Provider
        value={{
          currentUser,
          setCurrentUser,
          login,
          logout,
          isNew: isNew.current,
          isLoading,
          loadPartnerSessionUser,
        }}
      >
        {children}
      </AuthContext.Provider>

      {/* retry modal after 5 failed attempts */}
      <Dialog open={showRetryModal}>
        <DialogTitle>{t('Sign-In Error')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t(
              "We couldn't complete your sign-in after several tries. Please refresh the page to try again."
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRefresh} variant="contained" color="primary">
            {t('Refresh Page')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AuthProvider;
